import React, { useEffect, useState } from "react";
import { CheckCircle2, Loader2, MailWarning, X } from "lucide-react";
import { formatMoney, useCart } from "../../context/CartContext";
import { placeOrder } from "../../utils/orderApi";

const EMAIL_RE = /^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]{2,}$/;

const emptyForm = { name: "", phone: "", email: "", fulfillment: "pickup", address: "", paymentMethod: "" };

function validate(form) {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (form.phone.replace(/\D/g, "").length < 7) errors.phone = "Enter a valid phone number";
  if (!EMAIL_RE.test(form.email.trim())) errors.email = "Enter a valid email address";
  if (form.fulfillment === "delivery" && !form.address.trim()) errors.address = "Delivery address is required";
  if (!form.paymentMethod) errors.paymentMethod = "Choose cash or credit";
  return errors;
}

const inputClass = (hasError) =>
  `mt-1 w-full rounded-lg border px-3 py-2 text-sm text-gray-900 outline-none focus:ring-2 focus:ring-blue-500 ${
    hasError ? "border-red-400" : "border-gray-300"
  }`;

function Field({ label, error, children }) {
  return (
    <label className="block text-sm font-medium text-gray-700">
      {label}
      {children}
      {error && <span className="mt-1 block text-xs font-normal text-red-600">{error}</span>}
    </label>
  );
}

// Checkbox group where only one box can be ticked at a time (checkbox look, radio behaviour).
function CheckGroup({ legend, options, value, onChange, error }) {
  return (
    <fieldset>
      <legend className="text-sm font-medium text-gray-700">{legend}</legend>
      <div className="mt-1.5 flex flex-wrap gap-x-6 gap-y-2">
        {options.map((opt) => (
          <label key={opt.value} className="flex cursor-pointer items-center gap-2 text-sm text-gray-800">
            <input
              type="checkbox"
              checked={value === opt.value}
              onChange={() => onChange(value === opt.value ? "" : opt.value)}
              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            {opt.label}
          </label>
        ))}
      </div>
      {error && <span className="mt-1 block text-xs text-red-600">{error}</span>}
    </fieldset>
  );
}

export default function CheckoutModal({ customerId, onClose }) {
  const { lines, subtotalCents, taxCents, totalCents, clearCart } = useCart();
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && !submitting && onClose();
    window.addEventListener("keydown", onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose, submitting]);

  const set = (key) => (e) => {
    setForm((f) => ({ ...f, [key]: e.target.value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSubmitting(true);
    setSubmitError("");
    try {
      const data = await placeOrder({
        customerId,
        customer: {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          fulfillment: form.fulfillment,
          address: form.fulfillment === "delivery" ? form.address.trim() : "",
          paymentMethod: form.paymentMethod,
        },
        lines,
      });
      clearCart();
      setResult(data);
    } catch (err) {
      setErrors((prev) => ({ ...prev, ...err.fieldErrors }));
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center sm:items-center" role="dialog" aria-modal="true" aria-label="Checkout">
      <div className="absolute inset-0 bg-black/40" onClick={() => !submitting && onClose()} />
      <div className="relative flex max-h-[95vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl bg-white shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
          <h2 className="text-base font-semibold text-gray-900">{result ? "Order placed" : "Checkout"}</h2>
          <button type="button" onClick={onClose} disabled={submitting} className="rounded p-1 text-gray-500 hover:bg-gray-100 disabled:opacity-50" aria-label="Close checkout">
            <X className="h-5 w-5" />
          </button>
        </div>

        {result ? (
          <div className="space-y-3 overflow-y-auto px-5 py-8 text-center">
            <CheckCircle2 className="mx-auto h-12 w-12 text-green-600" />
            <p className="text-lg font-semibold text-gray-900">Thank you, your order is in!</p>
            <p className="text-sm text-gray-600">
              Order number <span className="font-semibold text-gray-900">#{result.orderNumber}</span>
            </p>
            {result.emailSent ? (
              <p className="text-sm text-gray-600">We've emailed your order PDF to {form.email.trim()}.</p>
            ) : (
              <p className="mx-auto flex max-w-sm items-start gap-2 rounded-lg bg-amber-50 p-3 text-left text-sm text-amber-800">
                <MailWarning className="mt-0.5 h-4 w-4 flex-none" />
                Your order was saved, but we couldn't send the confirmation email. Please keep your order number.
              </p>
            )}
            <button type="button" onClick={onClose} className="mt-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-700">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="flex min-h-0 flex-1 flex-col">
            <div className="space-y-4 overflow-y-auto px-5 py-4">
              <Field label="Name" error={errors.name}>
                <input type="text" value={form.name} onChange={set("name")} autoComplete="name" maxLength={100} className={inputClass(errors.name)} />
              </Field>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Phone number" error={errors.phone}>
                  <input type="tel" value={form.phone} onChange={set("phone")} autoComplete="tel" maxLength={25} className={inputClass(errors.phone)} />
                </Field>
                <Field label="Email address" error={errors.email}>
                  <input type="email" value={form.email} onChange={set("email")} autoComplete="email" maxLength={254} className={inputClass(errors.email)} />
                </Field>
              </div>

              <CheckGroup
                legend="Order type"
                value={form.fulfillment}
                onChange={(v) => {
                  setForm((f) => ({ ...f, fulfillment: v || "pickup" }));
                  setErrors((prev) => ({ ...prev, address: undefined }));
                }}
                options={[
                  { value: "pickup", label: "Pick up" },
                  { value: "delivery", label: "Delivery" },
                ]}
              />
              {form.fulfillment === "delivery" && (
                <Field label="Delivery address" error={errors.address}>
                  <textarea value={form.address} onChange={set("address")} rows={3} maxLength={300} autoComplete="street-address" className={inputClass(errors.address)} />
                </Field>
              )}

              <CheckGroup
                legend="Payment"
                value={form.paymentMethod}
                onChange={(v) => {
                  setForm((f) => ({ ...f, paymentMethod: v }));
                  setErrors((prev) => ({ ...prev, paymentMethod: undefined }));
                }}
                options={[
                  { value: "cash", label: "Cash" },
                  { value: "credit", label: "Credit" },
                ]}
                error={errors.paymentMethod}
              />

              <div className="space-y-1 rounded-lg bg-gray-50 p-3 text-sm">
                <div className="flex justify-between text-gray-600"><span>Subtotal</span><span className="tabular-nums">{formatMoney(subtotalCents)}</span></div>
                <div className="flex justify-between text-gray-600"><span>Tax</span><span className="tabular-nums">{formatMoney(taxCents)}</span></div>
                <div className="flex justify-between border-t border-gray-200 pt-1 font-semibold text-gray-900"><span>Total</span><span className="tabular-nums">{formatMoney(totalCents)}</span></div>
              </div>
              {submitError && <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700" role="alert">{submitError}</p>}
            </div>

            <div className="border-t border-gray-100 px-5 py-3">
              <button
                type="submit"
                disabled={submitting || lines.length === 0}
                className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {submitting ? "Placing order..." : `Place order · ${formatMoney(totalCents)}`}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
